import Link from '@alltrails/shared/denali/components/Link';
import Repeat from '@alltrails/shared/icons/Repeat';
import { defineMessages, FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import { toggleSubscriptionStatus } from 'api/My';
import CustomProvider from 'components/CustomProvider';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import useUser from 'hooks/useUser';
import RecurlyAccountStatus from 'types/RecurlyAccountStatus';
import * as styles from './AutoRenewal.module.scss';

type Props = {
  recurlyAccountStatus: RecurlyAccountStatus;
};

const messages = defineMessages({
  noTrial: {
    defaultMessage:
      'Are you sure? Your AllTrails+ subscription will expire on {date} and will not auto-renew. If you wish to cancel your current subscription, please email support@alltrails.com.'
  },
  trial: {
    defaultMessage: 'Are you sure? Your AllTrails+ trial will expire on {date} and will not auto-renew.'
  }
});

async function toggle(confirmText: string) {
  if (window.confirm(confirmText)) {
    await toggleSubscriptionStatus();
    window.location.reload();
  }
}

function TurnOnAutoRenew() {
  return (
    <Link
      onClick={async () => {
        // No need to show a prompt here so just go ahead and send the post request and reload
        await toggleSubscriptionStatus();
        window.location.reload();
      }}
      className={styles.link}
      size="md"
      icon={{ Component: Repeat }}
    >
      <FormattedMessage defaultMessage="Turn on auto-renew" />
    </Link>
  );
}

function TurnOffAutoRenew() {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();

  const formatter = new Intl.DateTimeFormat(languageRegionCode, { day: 'numeric', month: 'long', year: 'numeric' });

  const prompt = intl.formatMessage(user.subscription?.trial ? messages.trial : messages.noTrial, {
    date: formatter.format(new Date(user.expirationDate))
  });

  return (
    <Link
      onClick={() => {
        toggle(prompt);
      }}
      className={styles.link}
      size="md"
      icon={{ Component: Repeat }}
    >
      <FormattedMessage defaultMessage="Turn off auto-renew" />
    </Link>
  );
}

function CancelSubscription() {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();

  const formatter = new Intl.DateTimeFormat(languageRegionCode, { day: 'numeric', month: 'long', year: 'numeric' });

  const prompt = intl.formatMessage(
    defineMessages({
      message: {
        defaultMessage:
          'Are you sure? Your AllTrails+ subscription expired on {date} and has not been renewed. If you wish to continue your subscription, please update your Billing Info.'
      }
    }).message,
    {
      date: formatter.format(new Date(user.expirationDate))
    }
  );

  return (
    <Link
      onClick={() => {
        toggle(prompt);
      }}
      className={styles.link}
      size="md"
      icon={{ Component: Repeat }}
    >
      <FormattedMessage defaultMessage="Cancel subscription" />
    </Link>
  );
}

function AutoRenewalContent({ recurlyAccountStatus }: Props) {
  switch (recurlyAccountStatus) {
    case RecurlyAccountStatus.Active:
      return <TurnOffAutoRenew />;
    case RecurlyAccountStatus.PastDue:
      return <CancelSubscription />;
    case RecurlyAccountStatus.Cancelled:
      return <TurnOnAutoRenew />;
    default:
      return null;
  }
}

function AutoRenewal({ recurlyAccountStatus }: Props) {
  return (
    <CustomProvider>
      <AutoRenewalContent recurlyAccountStatus={recurlyAccountStatus} />
    </CustomProvider>
  );
}

export default AutoRenewal;
